var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { DialogHeader, DialogContent, DialogFooter, Box, useToast } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { object, string } from 'yup';
import { CancelButton, FormDialog, SubmitButton } from 'shared/domains/forms';
import { CreateErrorAlert } from './CreateErrorAlert';
import { CustomerIdField } from './CustomerIdField';
import { DescriptionField } from './DescriptionField';
import { NameField } from './NameField';
import { UpdateErrorAlert } from './UpdateErrorAlert';
import { useCreateProject } from './useCreateProject';
import { useUpdateProject } from './useUpdateProject';
export function ProjectDialog(props) {
    const { projectId, initialValues, onClose, onSubmit } = props;
    const { addToast } = useToast();
    const [createProject, { error: createError }] = useCreateProject();
    const [updateProject, { error: updateError }] = useUpdateProject();
    const initialFormValues = Object.assign({ name: '', description: '', customerId: '' }, initialValues);
    const schema = object().shape({
        name: string()
            .trim()
            .required(t({ message: 'Project name is required', comment: 'Create project dialog name field validation' })),
        description: string().trim(),
    });
    const handleCreateProject = (values) => __awaiter(this, void 0, void 0, function* () {
        try {
            const { data } = yield createProject(values);
            if (data === null || data === void 0 ? void 0 : data.createFileSystemProject.id) {
                onSubmit(data.createFileSystemProject);
            }
            addToast({
                appearance: 'success',
                title: t({
                    message: 'Project created',
                    comment: 'Create project dialog - Success toast title',
                }),
            });
        }
        catch (_a) {
            addToast({
                appearance: 'danger',
                title: t({
                    message: 'Failed to create project',
                    comment: 'Create project dialog - Error toast title',
                }),
            });
        }
    });
    const handleUpdateProject = (id, values) => __awaiter(this, void 0, void 0, function* () {
        try {
            const { data } = yield updateProject(id, values);
            if (data === null || data === void 0 ? void 0 : data.updateFileSystemProject.id) {
                onSubmit(data.updateFileSystemProject);
            }
            addToast({
                appearance: 'success',
                title: t({
                    message: 'Project updated',
                    comment: 'Edit project dialog - Success toast title',
                }),
            });
        }
        catch (_b) {
            addToast({
                appearance: 'danger',
                title: t({
                    message: 'Failed to update project',
                    comment: 'Edit project dialog - Error toast title',
                }),
            });
        }
    });
    const handleSubmit = (values) => __awaiter(this, void 0, void 0, function* () {
        if (projectId) {
            return handleUpdateProject(projectId, values);
        }
        return handleCreateProject(values);
    });
    const createTitle = t({
        message: 'Create new project',
        comment: 'Create project dialog - Header title',
    });
    const editTitle = t({
        message: 'Edit project',
        comment: 'Edit project dialog - Header title',
    });
    const createSubmitLabel = t({
        message: 'Create project',
        comment: 'Create project dialog - Submit button label',
    });
    const updateSubmitLabel = t({
        message: 'Save project',
        comment: 'Edit project dialog - Submit button label',
    });
    const dialogTitle = projectId ? editTitle : createTitle;
    const submitLabel = projectId ? updateSubmitLabel : createSubmitLabel;
    return (<FormDialog size="md" validateOnBlur={false} initialValues={initialFormValues} validationSchema={schema} onClose={onClose} onSubmit={handleSubmit}>
      <DialogHeader title={dialogTitle} icon={<Falcon icon="rectangle-history"/>}/>
      <DialogContent>
        <Box flexDirection="column" gap="lg">
          <NameField />
          <DescriptionField />
          <CustomerIdField />
          {createError && <CreateErrorAlert />}
          {updateError && <UpdateErrorAlert />}
        </Box>
      </DialogContent>
      <DialogFooter>
        <CancelButton size="small" onClick={onClose}>
          <Trans comment="Create project dialog - Cancel button label">Cancel</Trans>
        </CancelButton>
        <SubmitButton size="small">{submitLabel}</SubmitButton>
      </DialogFooter>
    </FormDialog>);
}
